import React, { useState, useEffect, useRef } from "react";
import mermaid from "mermaid";
import { useTheme } from "../../context/ThemeContext";
import { useLanguage } from "../../context/LanguageContext";
import {
  Workflow,
  Calendar,
  Coins,
  FileText,
  CheckCircle2,
  AlertCircle,
  Scale,
  Gavel,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  Code,
  Layers,
  ArrowRight
} from "lucide-react";

// Check if a string is a Mermaid diagram definition
export function isMermaidCode(text) {
  if (typeof text !== "string") return false;
  const trimmed = text.trim();
  return /^\s*(graph\s+(?:TD|TB|BT|RL|LR)|flowchart\s+(?:TD|TB|BT|RL|LR)|sequenceDiagram|stateDiagram(?:-v2)?|classDiagram|erDiagram|gantt|pie)\b/m.test(trimmed);
}

// Check if a string contains a multi-step arrow flow (e.g. text with ↓, ⬇, -->, ->, →, \rightarrow)
export function isArrowFlow(text) {
  if (typeof text !== "string") return false;
  // Match down arrows or connecting arrows
  const arrowMatches = text.match(/[↓⬇▼→➔➜]|\\rightarrow|\\to|(?:\s+-->\s+)|(?:\s+->\s+)|(?:^|\n)\s*-->\s*(?:\n|$)|(?:^|\n)\s*->\s*(?:\n|$)/g);
  if (!arrowMatches || arrowMatches.length < 1) return false;
  // Verify it has multiple text segments
  const segments = text.split(/(?:\r?\n\s*[↓⬇▼→➔➜]\s*(?:\r?\n|$))|(?:\s+[↓⬇▼→➔➜]\s+)|(?:\s*(?:\\rightarrow|\\to)\s*)|(?:\r?\n\s*-->\s*(?:\r?\n|$))|(?:\s+-->\s+)|(?:\r?\n\s*->\s*(?:\r?\n|$))|(?:\s+->\s+)/);
  const validSegments = segments
    .map((s) => s.replace(/^\s*\$\$|\$\$\s*$/g, "").replace(/\\text\{([^}]+)\}/g, "$1").replace(/\\mathrm\{([^}]+)\}/g, "$1").replace(/\$/g, "").trim())
    .filter((s) => s.length > 0 && !/^[↓⬇▼\->→➔➜]+$/.test(s));
  return validSegments.length >= 2;
}

// Parse text flow into structured timeline / flowchart steps
export function parseArrowFlow(raw) {
  const segments = raw.split(/(?:\r?\n\s*[↓⬇▼→➔➜]\s*(?:\r?\n|$))|(?:\s+[↓⬇▼→➔➜]\s+)|(?:\s*(?:\\rightarrow|\\to)\s*)|(?:\r?\n\s*-->\s*(?:\r?\n|$))|(?:\s+-->\s+)|(?:\r?\n\s*->\s*(?:\r?\n|$))|(?:\s+->\s+)/);
  const steps = [];

  const dateRegex = /\b(\d{1,2}\s+(?:Januari|Februari|Maret|April|Mei|Juni|Juli|Agustus|September|Oktober|November|Desember)\s+\d{4}|\b(?:19|20)\d{2}\b)/i;
  const amountRegex = /(?:Nilai\s+menurut\s+fiskus:\s*)?(?:Rp\.?\s*[\d\.,]+)/i;
  const docRegex = /\b(?:Nomor\s+[A-Z0-9\/\.\-]+|KEP-[A-Z0-9\/\.\-]+|SKP[A-Z0-9\/\.\-]*)/i;
  const statusRegex = /\b(dikabulkan(?:\s+sebagian|\s+seluruhnya)?|ditolak|dibatalkan|disetujui|terbit|diajukan)\b/i;

  for (let seg of segments) {
    seg = seg.replace(/^\s*\$\$|\$\$\s*$/g, "").replace(/\\text\{([^}]+)\}/g, "$1").replace(/\\mathrm\{([^}]+)\}/g, "$1").replace(/\$/g, "").trim();
    if (!seg || /^[↓⬇▼\->→➔➜]+$/.test(seg)) continue;

    const lines = seg.split("\n").map(l => l.trim()).filter(Boolean);
    if (!lines.length) continue;

    let title = lines[0];
    let date = null;
    let amount = null;
    let docRef = null;
    let status = null;
    const details = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      let matched = false;
      if (!date && dateRegex.test(line)) {
        date = line.match(dateRegex)[0];
        matched = true;
      }
      if (!amount && amountRegex.test(line)) {
        amount = line;
        matched = true;
      }
      if (!docRef && docRegex.test(line)) {
        docRef = line;
        matched = true;
      }
      if (!status && statusRegex.test(line)) {
        status = line;
        matched = true;
      }
      if (!matched) {
        details.push(line);
      }
    }

    // Categorize stage type for visual badge coloring
    let stageType = "default";
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes("pemeriksaan") || lowerTitle.includes("audit") || lowerTitle.includes("sp2")) {
      stageType = "audit";
    } else if (lowerTitle.includes("skp") || lowerTitle.includes("ketetapan") || lowerTitle.includes("stp")) {
      stageType = "assessment";
    } else if (lowerTitle.includes("keberatan") && !lowerTitle.includes("keputusan")) {
      stageType = "objection";
    } else if (lowerTitle.includes("banding") || lowerTitle.includes("pengadilan")) {
      stageType = "appeal";
    } else if (lowerTitle.includes("keputusan") || lowerTitle.includes("putusan") || lowerTitle.includes("surat keputusan")) {
      stageType = "decision";
    } else if (lowerTitle.includes("tahun pajak") || lowerTitle.includes("masa pajak") || lowerTitle.includes("spt")) {
      stageType = "tax_year";
    } else if (lowerTitle.includes("sengketa") || lowerTitle.includes("koreksi")) {
      stageType = "dispute";
    } else if (lowerTitle.includes("penelitian") || lowerTitle.includes("penelaahan")) {
      stageType = "investigation";
    } else if (lowerTitle.includes("tanggapan") || lowerTitle.includes("bantahan") || lowerTitle.includes("jawaban")) {
      stageType = "response";
    }

    steps.push({
      stepNumber: steps.length + 1,
      title,
      stageType,
      date,
      amount,
      docRef,
      status,
      details: details.join(" • ")
    });
  }

  return steps;
}

// Convert parsed steps into a clean Mermaid diagram definition
export function stepsToMermaid(steps) {
  if (!steps || !steps.length) return "";
  let lines = ["graph TD"];
  steps.forEach((s, idx) => {
    const nodeId = `N${idx + 1}`;
    let label = `${s.stepNumber}. ${s.title.replace(/"/g, "'")}`;
    if (s.date) label += `<br/><i>${s.date}</i>`;
    if (s.amount) label += `<br/><b>${s.amount.replace(/"/g, "'")}</b>`;
    if (s.docRef) label += `<br/>${s.docRef.replace(/"/g, "'")}`;
    lines.push(`  ${nodeId}["${label}"]`);
  });
  for (let i = 0; i < steps.length - 1; i++) {
    lines.push(`  N${i + 1} --> N${i + 2}`);
  }
  return lines.join("\n");
}

// Helper to determine node visual styling
function getStageStyle(stageType, isDark, isEnglish = false) {
  switch (stageType) {
    case "audit":
      return {
        badgeBg: isDark ? "bg-blue-500/20 text-blue-300 border-blue-400/40" : "bg-blue-50 text-[#1D4ED8] border-blue-200",
        nodeBg: isDark ? "bg-[#10243E] text-blue-200 border-blue-400 shadow-md shadow-blue-500/20" : "bg-blue-50 text-blue-800 border-blue-300",
        label: isEnglish ? "Tax Audit" : "Pemeriksaan",
        Icon: Scale
      };
    case "assessment":
      return {
        badgeBg: isDark ? "bg-rose-500/20 text-rose-300 border-rose-400/40" : "bg-red-50 text-[#B91C1C] border-red-200",
        nodeBg: isDark ? "bg-[#381014] text-rose-200 border-rose-400 shadow-md shadow-rose-500/20" : "bg-red-50 text-red-800 border-red-300",
        label: isEnglish ? "Tax Assessment" : "Ketetapan Pajak",
        Icon: FileText
      };
    case "objection":
      return {
        badgeBg: isDark ? "bg-amber-500/20 text-amber-300 border-amber-400/40" : "bg-amber-50 text-[#B45309] border-amber-200",
        nodeBg: isDark ? "bg-[#382008] text-amber-200 border-amber-400 shadow-md shadow-amber-500/20" : "bg-amber-50 text-amber-800 border-amber-300",
        label: isEnglish ? "Objection" : "Keberatan",
        Icon: AlertCircle
      };
    case "decision":
      return {
        badgeBg: isDark ? "bg-purple-500/20 text-purple-300 border-purple-400/40" : "bg-purple-50 text-[#6D28D9] border-purple-200",
        nodeBg: isDark ? "bg-[#28143E] text-purple-200 border-purple-400 shadow-md shadow-purple-500/20" : "bg-purple-50 text-purple-800 border-purple-300",
        label: isEnglish ? "Decision" : "Keputusan",
        Icon: Gavel
      };
    case "appeal":
      return {
        badgeBg: isDark ? "bg-pink-500/20 text-pink-300 border-pink-400/40" : "bg-pink-50 text-[#BE185D] border-pink-200",
        nodeBg: isDark ? "bg-[#381028] text-pink-200 border-pink-400 shadow-md shadow-pink-500/20" : "bg-pink-50 text-pink-800 border-pink-300",
        label: isEnglish ? "Tax Appeal" : "Banding Pengadilan",
        Icon: Gavel
      };
    case "dispute":
      return {
        badgeBg: isDark ? "bg-rose-500/20 text-rose-300 border-rose-400/40" : "bg-rose-50 text-[#BE123C] border-rose-200",
        nodeBg: isDark ? "bg-[#381018] text-rose-200 border-rose-400 shadow-md shadow-rose-500/20" : "bg-rose-50 text-rose-800 border-rose-300",
        label: isEnglish ? "Dispute Matter" : "Materi Sengketa",
        Icon: Coins
      };
    case "tax_year":
      return {
        badgeBg: isDark ? "bg-emerald-500/20 text-emerald-300 border-emerald-400/40" : "bg-emerald-50 text-[#15803D] border-emerald-200",
        nodeBg: isDark ? "bg-[#0E2A1E] text-emerald-200 border-emerald-400 shadow-md shadow-emerald-500/20" : "bg-emerald-50 text-emerald-800 border-emerald-300",
        label: isEnglish ? "Tax Year" : "Tahun Pajak",
        Icon: Calendar
      };
    default:
      return {
        badgeBg: isDark ? "bg-cyan-500/20 text-cyan-300 border-cyan-400/40" : "bg-gray-100 text-gray-700 border-gray-200",
        nodeBg: isDark ? "bg-[#10242E] text-cyan-200 border-cyan-400 shadow-md shadow-cyan-500/20" : "bg-gray-50 text-gray-800 border-gray-300",
        label: isEnglish ? "Process" : "Proses",
        Icon: Workflow
      };
  }
}

export default function FlowchartRenderer({ content = "", title = "Alur Proses & Kronologi Sengketa Pajak" }) {
  const { t, isEnglish } = useLanguage();
  const [viewMode, setViewMode] = useState("cards"); // 'cards' | 'mermaid' | 'text'
  const [layout, setLayout] = useState("vertical"); // 'vertical' | 'horizontal'
  const [copied, setCopied] = useState(false);
  const [mermaidSvg, setMermaidSvg] = useState("");
  const [mermaidError, setMermaidError] = useState(null);

  const containerRef = useRef(null);

  const themeContext = useTheme();
  const isDark = themeContext?.isDark ?? (typeof document !== "undefined" && (
    document.documentElement.classList.contains("dark") || 
    document.documentElement.getAttribute("data-theme") === "dark" ||
    document.documentElement.classList.contains("dark-theme")
  ));
  const isDirectMermaid = isMermaidCode(content);

  const parsedSteps = React.useMemo(() => {
    if (isDirectMermaid) return [];
    return parseArrowFlow(content);
  }, [content, isDirectMermaid]);

  const mermaidDefinition = React.useMemo(() => {
    if (isDirectMermaid) return content.trim();
    if (parsedSteps.length > 0) return stepsToMermaid(parsedSteps);
    return "";
  }, [content, isDirectMermaid, parsedSteps]);

  // Render Mermaid SVG
  useEffect(() => {
    if (!mermaidDefinition || (viewMode !== "mermaid" && !isDirectMermaid)) return;
    let isMounted = true;

    const renderChart = async () => {
      try {
        mermaid.initialize({
          startOnLoad: false,
          theme: isDark ? "dark" : "neutral",
          securityLevel: "loose",
          fontFamily: "'IBM Plex Sans', Inter, sans-serif",
          flowchart: {
            curve: "basis",
            useMaxWidth: true,
            htmlLabels: true
          },
          themeVariables: isDark
            ? {
                primaryColor: "#1E2A28",
                primaryTextColor: "#E8F0EC",
                primaryBorderColor: "#3F6B52",
                lineColor: "#528A6B",
                secondaryColor: "#16201E",
                tertiaryColor: "#111819"
              }
            : {
                primaryColor: "#F4F7F5",
                primaryTextColor: "#192320",
                primaryBorderColor: "#3F6B52",
                lineColor: "#3F6B52",
                secondaryColor: "#FFFFFF",
                tertiaryColor: "#DEE4DC"
              }
        });

        const id = `flowchart-${Math.random().toString(36).substring(2, 9)}`;
        const { svg } = await mermaid.render(id, mermaidDefinition);
        if (isMounted) {
          setMermaidSvg(svg);
          setMermaidError(null);
        }
      } catch (err) {
        if (isMounted) {
          console.warn("Mermaid rendering warning:", err);
          setMermaidError(err.message || "Gagal merender diagram Mermaid");
        }
      }
    };

    renderChart();
    return () => {
      isMounted = false;
    };
  }, [mermaidDefinition, viewMode, isDirectMermaid, isDark]);

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // If pure mermaid was passed and cards view is empty, default view to mermaid
  const activeView = isDirectMermaid && viewMode === "cards" ? "mermaid" : viewMode;

  return (
    <div
      ref={containerRef}
      className={`my-4 overflow-hidden rounded-2xl border transition-all ${
        isDark
          ? "border-[#243538] bg-[#121A1D] text-[#F8FAFC] shadow-xl shadow-black/40"
          : "border-[#DEE4DC] bg-[#FCFDFD] text-[#192320] shadow-sm"
      }`}
    >
      {/* HEADER BAR */}
      <div
        className={`flex flex-wrap items-center justify-between gap-2.5 border-b px-4 py-3 sm:px-5 ${
          isDark ? "border-[#243538] bg-[#162226]" : "border-[#E5ECE8] bg-[#F5F8F6]"
        }`}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${
              isDark
                ? "border-[#38BDF8]/40 bg-[#38BDF8]/20 text-[#38BDF8]"
                : "border-[#3F6B52]/30 bg-[#3F6B52]/10 text-[#3F6B52]"
            }`}
          >
            <Workflow size={17} />
          </div>
          <div className="min-w-0">
            <h4
              className="text-[13.5px] sm:text-[14px] font-bold tracking-tight truncate"
              style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
            >
              {title === "Alur Proses & Kronologi Sengketa Pajak" ? t("flowchart.title") : title}
            </h4>
            <div className="flex items-center gap-2 text-[11px] opacity-75">
              <span>{isEnglish ? "Interactive Chronological Timeline" : "Alur Kronologis Interaktif"}</span>
              {parsedSteps.length > 0 && (
                <>
                  <span>•</span>
                  <span className="font-semibold text-brand">
                    {parsedSteps.length} {isEnglish ? "Stages" : "Tahapan"}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* CONTROLS */}
        <div className="flex items-center gap-1.5 shrink-0 ml-auto">
          {/* View Mode Toggle */}
          <div
            className={`flex items-center rounded-lg p-0.5 border text-[11.5px] font-medium ${
              isDark ? "border-[#243538] bg-[#101719]" : "border-[#DEE4DC] bg-white"
            }`}
          >
            {!isDirectMermaid && (
              <button
                type="button"
                onClick={() => setViewMode("cards")}
                className={`flex items-center gap-1 rounded-md px-2.5 py-1 transition-all ${
                  activeView === "cards"
                    ? isDark
                      ? "bg-[#38BDF8] text-slate-950 font-bold shadow-xs"
                      : "bg-[#3F6B52] text-white shadow-xs"
                    : isDark
                    ? "text-[#94A3B8] hover:text-white"
                    : "text-[#5C6B66] hover:text-[#192320]"
                }`}
                title={t("flowchart.viewVisual")}
              >
                <Layers size={12} />
                <span className="hidden sm:inline">{t("flowchart.tabFlowchart")}</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => setViewMode("mermaid")}
              className={`flex items-center gap-1 rounded-md px-2.5 py-1 transition-all ${
                activeView === "mermaid"
                  ? isDark
                    ? "bg-[#38BDF8] text-slate-950 font-bold shadow-xs"
                    : "bg-[#3F6B52] text-white shadow-xs"
                  : isDark
                  ? "text-[#94A3B8] hover:text-white"
                  : "text-[#5C6B66] hover:text-[#192320]"
              }`}
              title={t("flowchart.viewMermaid")}
            >
              <Workflow size={12} />
              <span className="hidden sm:inline">{t("flowchart.tabDiagram")}</span>
            </button>

            <button
              type="button"
              onClick={() => setViewMode("text")}
              className={`flex items-center gap-1 rounded-md px-2.5 py-1 transition-all ${
                activeView === "text"
                  ? isDark
                    ? "bg-[#38BDF8] text-slate-950 font-bold shadow-xs"
                    : "bg-[#3F6B52] text-white shadow-xs"
                  : isDark
                  ? "text-[#94A3B8] hover:text-white"
                  : "text-[#5C6B66] hover:text-[#192320]"
              }`}
              title={t("flowchart.viewText")}
            >
              <Code size={12} />
              <span className="hidden sm:inline">{t("flowchart.tabText")}</span>
            </button>
          </div>

          {/* Vertical / Horizontal toggle for cards view */}
          {activeView === "cards" && parsedSteps.length > 0 && (
            <button
              type="button"
              onClick={() => setLayout(layout === "vertical" ? "horizontal" : "vertical")}
              className={`flex items-center gap-1 rounded-lg border px-2 py-1 text-[11.5px] font-medium transition-all ${
                isDark
                  ? "border-[#243538] bg-[#101719] text-[#CBD5E1] hover:text-white hover:border-[#38BDF8]/40"
                  : "border-[#DEE4DC] bg-white text-[#5C6B66] hover:text-[#192320]"
              }`}
              title={layout === "vertical" ? t("flowchart.switchHorizontal") : t("flowchart.switchVertical")}
            >
              {layout === "vertical" ? (
                <>
                  <ArrowRight size={12} />
                  <span className="hidden md:inline">{t("flowchart.horizontal")}</span>
                </>
              ) : (
                <>
                  <ChevronDown size={12} />
                  <span className="hidden md:inline">{t("flowchart.vertical")}</span>
                </>
              )}
            </button>
          )}

          {/* Copy Button */}
          <button
            type="button"
            onClick={handleCopy}
            className={`flex items-center gap-1 rounded-lg border p-1.5 text-[11.5px] transition-all ${
              isDark
                ? "border-[#243538] bg-[#101719] text-[#CBD5E1] hover:text-white hover:border-[#38BDF8]/40"
                : "border-[#DEE4DC] bg-white text-[#5C6B66] hover:text-[#192320]"
            }`}
            title={t("flowchart.copyFlow")}
          >
            {copied ? <Check size={13} className="text-[#34D399]" /> : <Copy size={13} />}
          </button>
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="p-4 sm:p-6">
        {/* VIEW 1: EXECUTIVE CARDS FLOWCHART */}
        {activeView === "cards" && parsedSteps.length > 0 && (
          <div>
            {layout === "vertical" ? (
              /* VERTICAL PIPELINE (ANGKA TO ANGKA) */
              <div className="flex flex-col">
                {parsedSteps.map((step, idx) => {
                  const isLast = idx === parsedSteps.length - 1;
                  const style = getStageStyle(step.stageType, isDark);
                  const IconComponent = style.Icon;

                  return (
                    <div key={idx} className="flex items-stretch gap-3.5 sm:gap-4 group">
                      {/* Timeline Axis Column: Angka to Angka with Straight Centered Line */}
                      <div className="flex flex-col items-center shrink-0 w-8 sm:w-9">
                        {/* Circle Number */}
                        <div
                          className={`flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-full border-2 text-xs sm:text-[13px] font-bold transition-transform group-hover:scale-105 shadow-xs z-10 ${
                            style.nodeBg
                          }`}
                        >
                          {step.stepNumber}
                        </div>

                        {/* Direct Vertical Connecting Line between Numbers */}
                        {!isLast && (
                          <div className="flex-1 flex flex-col items-center w-full my-0.5">
                            {/* Line segment top */}
                            <div className={`w-0.5 flex-1 min-h-[14px] ${isDark ? "bg-[#243538]" : "bg-[#DEE4DC]"}`} />
                            {/* Directional Arrow aligned strictly on the line */}
                            <div
                              className={`my-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border text-[9px] shadow-2xs ${
                                isDark
                                  ? "border-[#38BDF8]/40 bg-[#141E21] text-[#38BDF8]"
                                  : "border-[#3F6B52]/30 bg-white text-[#3F6B52]"
                              }`}
                              title="Menuju tahap berikutnya"
                            >
                              <ChevronDown size={10} />
                            </div>
                            {/* Line segment bottom */}
                            <div className={`w-0.5 flex-1 min-h-[14px] ${isDark ? "bg-[#243538]" : "bg-[#DEE4DC]"}`} />
                          </div>
                        )}
                      </div>

                      {/* Step Card Content */}
                      <div className={`flex-1 min-w-0 ${!isLast ? "pb-4 sm:pb-5" : ""}`}>
                        <div
                          className={`rounded-xl border p-3.5 sm:p-4 transition-all hover:shadow-md ${
                            isDark
                              ? "border-[#243538] bg-[#141E21] hover:border-[#38BDF8]/60 hover:shadow-lg hover:shadow-[#38BDF8]/5"
                              : "border-[#E2EAE5] bg-white hover:border-[#3F6B52]/40"
                          }`}
                        >
                          <div className="flex flex-wrap items-start justify-between gap-2 mb-1.5">
                            <h5 className="text-[14px] sm:text-[15px] font-bold text-text-pri leading-snug">
                              {step.title}
                            </h5>

                            <span
                              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold border ${
                                style.badgeBg
                              }`}
                            >
                              <IconComponent size={11} />
                              {style.label}
                            </span>
                          </div>

                          {/* Badges: Date, Document Number, Amount, Status */}
                          <div className="flex flex-wrap items-center gap-1.5 mt-2">
                            {step.date && (
                              <span
                                className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium border ${
                                  isDark
                                    ? "border-blue-400/30 bg-blue-500/20 text-blue-200"
                                    : "border-blue-200 bg-blue-50 text-blue-800"
                                }`}
                              >
                                <Calendar size={11} className="shrink-0" />
                                {step.date}
                              </span>
                            )}

                            {step.amount && (
                              <span
                                className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold border ${
                                  isDark
                                    ? "border-rose-400/30 bg-rose-500/20 text-rose-200"
                                    : "border-red-200 bg-red-50 text-red-800"
                                }`}
                              >
                                <Coins size={11} className="shrink-0" />
                                {step.amount}
                              </span>
                            )}

                            {step.docRef && (
                              <span
                                className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium border ${
                                  isDark
                                    ? "border-purple-400/30 bg-purple-500/20 text-purple-200"
                                    : "border-purple-200 bg-purple-50 text-purple-800"
                                }`}
                              >
                                <FileText size={11} className="shrink-0" />
                                {step.docRef}
                              </span>
                            )}

                            {step.status && (
                              <span
                                className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium border ${
                                  isDark
                                    ? "border-amber-400/30 bg-amber-500/20 text-amber-200"
                                    : "border-amber-200 bg-amber-50 text-amber-800"
                                }`}
                              >
                                <CheckCircle2 size={11} className="shrink-0" />
                                {step.status}
                              </span>
                            )}
                          </div>

                          {/* Extra Details */}
                          {step.details && (
                            <p className={`mt-2 text-[12.5px] leading-relaxed ${isDark ? "text-[#CBD5E1]" : "text-[#5C6B66]"}`}>
                              {step.details}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* HORIZONTAL PIPELINE */
              <div className="flex items-stretch gap-3 overflow-x-auto pb-4 pt-1">
                {parsedSteps.map((step, idx) => {
                  const isLast = idx === parsedSteps.length - 1;
                  const style = getStageStyle(step.stageType, isDark);
                  const IconComponent = style.Icon;

                  return (
                    <React.Fragment key={idx}>
                      <div
                        className={`flex w-64 shrink-0 flex-col justify-between rounded-xl border p-4 transition-all hover:shadow-md ${
                          isDark
                            ? "border-[#243538] bg-[#141E21] hover:border-[#38BDF8]/60 hover:shadow-lg"
                            : "border-[#E2EAE5] bg-white hover:border-[#3F6B52]/40"
                        }`}
                      >
                        <div>
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <span
                              className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold border ${style.nodeBg}`}
                            >
                              {step.stepNumber}
                            </span>
                            <span
                              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border ${style.badgeBg}`}
                            >
                              <IconComponent size={10} />
                              {style.label}
                            </span>
                          </div>

                          <h5 className="text-[13.5px] font-bold text-text-pri line-clamp-2 mb-2">
                            {step.title}
                          </h5>

                          {step.date && (
                            <div className={`flex items-center gap-1 text-[11px] mb-1 ${isDark ? "text-blue-300" : "text-blue-700"}`}>
                              <Calendar size={11} className={isDark ? "text-[#38BDF8]" : "text-[#3273D9]"} />
                              <span>{step.date}</span>
                            </div>
                          )}

                          {step.amount && (
                            <div className={`text-[11px] font-semibold mb-1 ${isDark ? "text-rose-400" : "text-[#C41E24]"}`}>
                              {step.amount}
                            </div>
                          )}

                          {step.docRef && (
                            <div className={`text-[10.5px] font-mono mb-1 truncate ${isDark ? "text-[#CBD5E1]" : "text-gray-600"}`} title={step.docRef}>
                              {step.docRef}
                            </div>
                          )}
                        </div>

                        {step.status && (
                          <div className={`mt-2 pt-2 border-t text-[11px] font-medium flex items-center gap-1 ${
                            isDark ? "border-[#243538] text-amber-300" : "border-ledger-border/40 text-[#D97706]"
                          }`}>
                            <CheckCircle2 size={11} />
                            <span className="truncate">{step.status}</span>
                          </div>
                        )}
                      </div>

                      {!isLast && (
                        <div className={`flex shrink-0 items-center justify-center ${isDark ? "text-[#38BDF8] opacity-80" : "text-brand opacity-60"}`}>
                          <ChevronRight size={20} />
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* VIEW 2: MERMAID VECTOR GRAPH */}
        {activeView === "mermaid" && (
          <div className="overflow-x-auto py-2">
            {mermaidError ? (
              <div
                className={`rounded-xl border p-4 text-xs font-mono ${
                  isDark ? "border-red-900/40 bg-red-950/20 text-red-300" : "border-red-200 bg-red-50 text-red-700"
                }`}
              >
                <div className="font-bold mb-1">Peringatan Render Diagram:</div>
                <div>{mermaidError}</div>
                <div className="mt-2 text-[11px] opacity-80">
                  Diagram dialihkan ke format tampilan Flowchart Kartu di tab sebelah.
                </div>
              </div>
            ) : mermaidSvg ? (
              <div
                dangerouslySetInnerHTML={{ __html: mermaidSvg }}
                className="flex justify-center min-w-full [&_svg]:max-w-full [&_svg]:h-auto"
              />
            ) : (
              <div className="flex items-center justify-center py-8 text-sm opacity-60">
                Memuat diagram visual...
              </div>
            )}
          </div>
        )}

        {/* VIEW 3: RAW TEXT */}
        {activeView === "text" && (
          <div
            className={`rounded-xl border p-4 font-mono text-[12.5px] leading-relaxed overflow-x-auto ${
              isDark ? "border-[#243538] bg-[#0B1113] text-[#CBD5E1]" : "border-[#DEE4DC] bg-gray-50 text-gray-800"
            }`}
          >
            <pre className="whitespace-pre-wrap">{content}</pre>
          </div>
        )}
      </div>
    </div>
  );
}
